import { authFetch } from "@/lib/api-client";
import { API_REQUEST_TIMEOUT_LONG_MS } from "@/lib/query-constants";

import type {
  PersonaOpportunityKpis,
  PersonaOpportunitySetType,
  PersonaOpportunityStatus,
  PersonaOpportunitySupportingAd,
} from "./persona-opportunity-sets.api";

// ─── Types ──────────────────────────────────────────────────────────
//
// The Opportunity Hub lists rows from several sets at once. Each row therefore
// carries its own set and persona identity: there is no single selected set to
// read them from.

export interface HubOpportunityRow {
  id: string;
  setId: string;
  setName: string;
  setType: PersonaOpportunitySetType;
  /** Denominator for "N of M competitors run it", derived from the set config. */
  competitorsTotal: number;
  /** KPI stick-rate window for this set; null when the set config has no override. */
  stickDays: number | null;
  personaId: string | null;
  personaTitle: string | null;
  /** Signed URL for the generated avatar; null when the persona has none. */
  personaAvatarUrl: string | null;
  /** ISO timestamp once BigQuery stopped returning this persona; the row still shows. */
  personaStaledAt: string | null;
  status: PersonaOpportunityStatus;
  angle: string | null;
  topicTitle: string | null;
  painPoint: string | null;
  motivation: string | null;
  drivenBy: "painpoint" | "motivation" | null;
  action: string | null;
  /** COMPETITOR-driven columns — null on persona rows. */
  dataset: string | null;
  feature: string | null;
  subFeature: string | null;
  engine: string | null;
  topicDescription: string | null;
  findings: string | null;
  suggestion: string | null;
  benchmarkSpend: number | null;
  competitorSpend: number | null;
  spendPerAngle: number | null;
  benchmarkSpendPerAngle: number | null;
  competitorSpendPerAngle: number | null;
  kpi: number | null;
  competitorKpi: number | null;
  benchmarkKpi: number | null;
  industry: string | null;
  state: "active" | "rejected" | null;
  runDate: string | null;
  rank: number;
  isCustom: boolean;
  kpis: PersonaOpportunityKpis;
  spend: number | null;
  supportingAd: Pick<
    PersonaOpportunitySupportingAd,
    "id" | "hash" | "competitor" | "url" | "thumbnailUrl" | "profilePictureUrl" | "daysLive" | "rank"
  > | null;
  createdAt: string;
  updatedAt: string;
  /** Winning-ad metrics for a Post-Flight row. Omitted on other set types. */
  metadata?: unknown;
}

export interface HubOpportunitiesPage {
  items: HubOpportunityRow[];
  nextCursor: string | null;
  hasMore: boolean;
}

export type HubOpportunityGroupBy = "persona" | "angle" | "set" | "type" | "feature";

export interface HubOpportunityGroupSummary {
  key: string;
  title: string;
  avatarUrl: string | null;
  count: number;
  spend: number;
  creativesCount: number;
  competitorsRunningCount: number;
  competitorsTotal: number;
  stickRatePct: number | null;
  duplicatedPct: number | null;
  investmentIndex: number | null;
  who?: string | null;
  personalityKey?: string | null;
  pains?: string[];
  motivations?: string[];
  isStale?: boolean;
  setId?: string | null;
  setName?: string | null;
}

export interface HubOpportunityGroupsPage {
  groups: HubOpportunityGroupSummary[];
  nextCursor: string | null;
  hasMore: boolean;
}

export interface HubOpportunityListFilters {
  search?: string;
  setId?: string;
  setType?: "PERSONA" | "COMPETITOR";
  status?: string;
  action?: string;
  dataset?: string;
  feature?: string;
  subFeature?: string;
  engine?: string;
  /** `columnId:asc` or `columnId:desc`. */
  sort?: string;
}

export type HubDocumentKind = "SCRIPT" | "BRIEF";

export interface HubDocumentRow {
  id: string;
  kind: HubDocumentKind;
  title: string | null;
  slotIndex: number;
  /** The script a hook variant was cut from; null for briefs and root scripts. */
  parentScriptId: string | null;
  isFavorite: boolean;
  /** Null or absent is Meta. */
  youtubeFormat?: "in_stream_16_9" | "shorts_9_16" | null;
  /** Scripts carry a generation status; briefs are persisted only once complete. */
  status: string | null;
  /** Script concept line; null for briefs. */
  concept: string | null;
  duration: string | null;
  /** Scenes in the latest scene version; null for briefs. */
  sceneCount: number | null;
  /** Brief hook package size; null for scripts. */
  hookPackage: number | null;
  /** Hooks at the script's latest version; null for briefs. */
  hookCount?: number | null;
  /** Latest version number of the script or brief. */
  versionCount: number;
  opportunityId: string;
  opportunityLabel: string | null;
  setId: string;
  setName: string;
  setType: PersonaOpportunitySetType;
  createdAt: string;
  updatedAt: string;
}

/** Collapsed opportunity header on Scripts & Briefs. */
export interface HubDocumentGroup {
  opportunityId: string;
  opportunityLabel: string | null;
  personaTitle: string | null;
  /** The set's "Your Scripts" bucket, listed ahead of every generated angle. */
  isCustom: boolean;
  setId: string;
  setName: string;
  setType: PersonaOpportunitySetType;
  scriptCount: number;
  briefCount: number;
}

export interface HubDocumentGroupsPage {
  groups: HubDocumentGroup[];
  nextOffset: number | null;
  hasMore: boolean;
}

export interface HubDocumentCursors {
  scriptCursor?: string;
  briefCursor?: string;
}

export interface HubDocumentsPage {
  items: HubDocumentRow[];
  scriptCursor: string | null;
  briefCursor: string | null;
  hasMore: boolean;
}

// ─── Read Endpoints ─────────────────────────────────────────────────

function setIdsParam(setIds: readonly string[]): string {
  return setIds.join(",");
}

function appendHubFilters(qs: URLSearchParams, filters: HubOpportunityListFilters | undefined) {
  if (!filters) return;
  if (filters.search) qs.set("search", filters.search);
  if (filters.setId) qs.set("setId", filters.setId);
  if (filters.setType) qs.set("setType", filters.setType);
  if (filters.status) qs.set("status", filters.status);
  if (filters.action) qs.set("action", filters.action);
  if (filters.dataset) qs.set("dataset", filters.dataset);
  if (filters.feature) qs.set("feature", filters.feature);
  if (filters.subFeature) qs.set("subFeature", filters.subFeature);
  if (filters.engine) qs.set("engine", filters.engine);
  if (filters.sort) qs.set("sort", filters.sort);
}

export interface FetchHubOpportunitiesParams {
  /** Empty lists every live set of the tenant — the hub's unfiltered view. */
  setIds: readonly string[];
  cursor?: string;
  limit?: number;
  groupBy?: HubOpportunityGroupBy;
  groupKey?: string;
  filters?: HubOpportunityListFilters;
}

export async function fetchHubOpportunities(
  params: FetchHubOpportunitiesParams,
): Promise<HubOpportunitiesPage> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.groupBy) qs.set("groupBy", params.groupBy);
  if (params.groupKey) qs.set("groupKey", params.groupKey);
  appendHubFilters(qs, params.filters);
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<HubOpportunitiesPage>(
    `/persona-opportunity-sets/hub/opportunities${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export interface FetchHubOpportunityGroupsParams {
  setIds: readonly string[];
  groupBy: HubOpportunityGroupBy;
  cursor?: string;
  limit?: number;
  filters?: HubOpportunityListFilters;
}

export async function fetchHubOpportunityGroups(
  params: FetchHubOpportunityGroupsParams,
): Promise<HubOpportunityGroupsPage> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  qs.set("groupBy", params.groupBy);
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  appendHubFilters(qs, params.filters);
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<HubOpportunityGroupsPage>(
    `/persona-opportunity-sets/hub/opportunity-groups${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export interface FetchHubDocumentsParams {
  setIds: readonly string[];
  kind?: "all" | "script" | "brief";
  scriptCursor?: string;
  briefCursor?: string;
  limit?: number;
  opportunityId?: string;
}

export interface FetchHubDocumentGroupsParams {
  setIds: readonly string[];
  opportunityIds: readonly string[];
  offset?: number;
  limit?: number;
}

export interface HubOpportunityFacets {
  actions: string[];
  datasets: string[];
  features: string[];
  subFeatures: string[];
  engines: string[];
}

export async function fetchHubOpportunityFacets(params: {
  setIds: readonly string[];
  dataset?: string;
}): Promise<HubOpportunityFacets> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  if (params.dataset && params.dataset !== "all") qs.set("dataset", params.dataset);
  const query = qs.size > 0 ? `?${qs}` : "";
  const { data } = await authFetch<HubOpportunityFacets>(
    `/persona-opportunity-sets/hub/opportunity-facets${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export async function fetchHubDocuments(
  params: FetchHubDocumentsParams,
): Promise<HubDocumentsPage> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  if (params.kind && params.kind !== "all") qs.set("kind", params.kind);
  if (params.scriptCursor) qs.set("scriptCursor", params.scriptCursor);
  if (params.briefCursor) qs.set("briefCursor", params.briefCursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.opportunityId) qs.set("opportunityId", params.opportunityId);
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<HubDocumentsPage>(
    `/persona-opportunity-sets/hub/documents${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export interface HubTabCounts {
  sets: number;
  opportunities: number;
  scripts: number;
  sent_to_vendor: number;
}

export async function fetchHubTabCounts(params: {
  setIds: readonly string[];
}): Promise<HubTabCounts> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  const query = qs.size > 0 ? `?${qs}` : "";
  const { data } = await authFetch<HubTabCounts>(
    `/persona-opportunity-sets/hub/tab-counts${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}

export async function fetchHubDocumentGroups(
  params: FetchHubDocumentGroupsParams,
): Promise<HubDocumentGroupsPage> {
  const qs = new URLSearchParams();
  if (params.setIds.length > 0) qs.set("setIds", setIdsParam(params.setIds));
  if (params.opportunityIds.length > 0) {
    qs.set("opportunityIds", params.opportunityIds.join(","));
  }
  if (params.offset) qs.set("offset", String(params.offset));
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.size > 0 ? `?${qs}` : "";

  const { data } = await authFetch<HubDocumentGroupsPage>(
    `/persona-opportunity-sets/hub/document-groups${query}`,
    { timeoutMs: API_REQUEST_TIMEOUT_LONG_MS },
  );
  return data;
}
